import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import type {
    DriverProfile,
    DrivingStyle,
    Interest,
    InterestCategory,
    Feed,
    ContactsList
} from '../../types/networking';

interface NetworkingState {
    // Профиль пользователя
    profile: DriverProfile | null;
    hasProfile: boolean;
    profileLoading: boolean;

    // Справочники
    drivingStyles: DrivingStyle[];
    interests: Interest[];
    interestCategories: InterestCategory[];

    // Лента
    feed: Feed | null;
    feedLoading: boolean;

    // Контакты
    contacts: ContactsList | null;
    contactsLoading: boolean;

    // UI состояния
    showBanner: boolean;
    showProfileCreation: boolean;
}

const initialState: NetworkingState = {
    profile: null,
    hasProfile: false,
    profileLoading: false,

    drivingStyles: [],
    interests: [],
    interestCategories: [],

    feed: null,
    feedLoading: false,

    contacts: null,
    contactsLoading: false,

    showBanner: false,
    showProfileCreation: false,
};

const networkingSlice = createSlice({
    name: 'networking',
    initialState,
    reducers: {
        // Профиль
        setProfile: (state, action: PayloadAction<DriverProfile | null>) => {
            state.profile = action.payload;
            state.hasProfile = !!action.payload;
        },
        setProfileLoading: (state, action: PayloadAction<boolean>) => {
            state.profileLoading = action.payload;
        },

        // Справочники
        setDrivingStyles: (state, action: PayloadAction<DrivingStyle[]>) => {
            state.drivingStyles = action.payload;
        },
        setInterests: (state, action: PayloadAction<Interest[]>) => {
            state.interests = action.payload;
        },
        setInterestCategories: (state, action: PayloadAction<InterestCategory[]>) => {
            state.interestCategories = action.payload;
        },

        // Лента
        setFeed: (state, action: PayloadAction<Feed | null>) => {
            state.feed = action.payload;
        },
        appendFeed: (state, action: PayloadAction<Feed>) => {
            if (state.feed) {
                state.feed.profiles = [...state.feed.profiles, ...action.payload.profiles];
                state.feed.has_more = action.payload.has_more;
                state.feed.total_count = action.payload.total_count;
            } else {
                state.feed = action.payload;
            }
        },
        setFeedLoading: (state, action: PayloadAction<boolean>) => {
            state.feedLoading = action.payload;
        },

        // Контакты
        setContacts: (state, action: PayloadAction<ContactsList | null>) => {
            state.contacts = action.payload;
        },
        setContactsLoading: (state, action: PayloadAction<boolean>) => {
            state.contactsLoading = action.payload;
        },

        // UI
        setShowBanner: (state, action: PayloadAction<boolean>) => {
            state.showBanner = action.payload;
        },
        setShowProfileCreation: (state, action: PayloadAction<boolean>) => {
            state.showProfileCreation = action.payload;
        },

        // Очистка
        clearNetworking: (state) => {
            state.profile = null;
            state.hasProfile = false;
            state.feed = null;
            state.contacts = null;
        },
    },
});

export const {
    setProfile,
    setProfileLoading,
    setDrivingStyles,
    setInterests,
    setInterestCategories,
    setFeed,
    appendFeed,
    setFeedLoading,
    setContacts,
    setContactsLoading,
    setShowBanner,
    setShowProfileCreation,
    clearNetworking,
} = networkingSlice.actions;

export const networkingReducer = networkingSlice.reducer;